import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const CraftsmanADUDesignSaratoga = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Craftsman ADU Design & Construction in Saratoga",
    "description": "Custom Craftsman ADUs in Saratoga. Expert design & construction by UDA ADU. Get your free consultation today.",
    "publisher": {
      "@type": "Organization",
      "name": "UDA ADU",
      "url": "https://www.udaadu.com"
    },
    "url": "https://www.udaadu.com/craftsman-adu-design-saratoga"
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/craftsman-adu-design-saratoga" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData)
      }} />
      
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button onClick={closeCalendlyModal} className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors">
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" width="100%" height="100%" frameBorder="0" title="Schedule a meeting" />
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <section className="text-center py-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-white">
            Craftsman ADU Design & Construction in Saratoga
          </h1>
          <p className="text-xl md:text-2xl leading-relaxed max-w-4xl mx-auto text-gray-300 mb-8">
            Create enchanting Craftsman ADUs in Saratoga that celebrate the Village character with handcrafted details and natural materials that honor the community's artistic heritage.
          </p>
          <Button onClick={openCalendlyModal} className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-4 px-8 rounded-xl shadow-lg transition-colors text-lg">
            Get Free Consultation
          </Button>
        </section>

        {/* Why Choose Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Why Choose Craftsman ADUs in Saratoga?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Village Character</h3>
              <p className="text-gray-300">Craftsman design perfectly captures Saratoga's charming village atmosphere, creating ADUs that feel naturally integrated with the community's artistic spirit.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Artisan Heritage</h3>
              <p className="text-gray-300">Honor Saratoga's history of craftsmanship and artistic appreciation with authentic handcrafted details and premium natural materials.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Garden Integration</h3>
              <p className="text-gray-300">Craftsman design harmonizes beautifully with Saratoga's lush gardens and natural landscape, creating peaceful retreats that celebrate nature.</p>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Craftsman ADU Features & Design Elements
          </h2>
          <div className="bg-white rounded-xl p-8 max-w-4xl mx-auto">
            <ul className="space-y-4 text-gray-800">
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Traditional timber framing with exposed structural elements</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Handcrafted built-ins and custom millwork throughout</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Inviting covered porches perfect for garden views</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Natural stone foundations and landscape integration</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Warm wood finishes and authentic hardware details</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Multi-pane windows with garden and mountain views</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Local Considerations */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Saratoga-Specific Craftsman ADU Considerations
          </h2>
          <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8 max-w-4xl mx-auto">
            <p className="text-gray-300 mb-6">
              Building Craftsman ADUs in Saratoga requires understanding of village character and natural preservation standards:
            </p>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Saratoga's village-scale development and character guidelines</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Historic district compatibility and preservation requirements</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Santa Clara County building codes and hillside regulations</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Tree preservation and heritage oak protection standards</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Fire safety requirements for wildland interface areas</span>
              </li>
            </ul>
            <p className="text-gray-300 mt-6">
              Our expertise in Saratoga's unique village character ensures your Craftsman ADU enhances the community's charm while meeting all regulatory requirements.
            </p>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            What to Expect: Craftsman ADU Design Process
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-6">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Village-Sensitive Design</h3>
              <p className="text-gray-300">We create Craftsman designs that honor Saratoga's village character while integrating seamlessly with your property's natural features.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-6">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Thoughtful Permitting</h3>
              <p className="text-gray-300">Our team navigates Saratoga's design review process and environmental requirements with careful attention to community standards.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-6">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Artisan Execution</h3>
              <p className="text-gray-300">Master craftsmen execute traditional techniques with attention to detail that reflects Saratoga's appreciation for fine craftsmanship.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-6">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Harmonious Completion</h3>
              <p className="text-gray-300">From planning through final details, we deliver a Craftsman ADU that enhances your Saratoga property's natural beauty and village character.</p>
            </div>
          </div>
        </section>

        {/* Cost Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Craftsman ADU Cost in Saratoga
          </h2>
          <div className="text-center">
            <Link to="/adu-calculator">
              <Button className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-6 px-12 rounded-xl shadow-lg text-xl">
                Use Our ADU Cost Calculator
              </Button>
            </Link>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
            Ready to Build Your Craftsman ADU in Saratoga?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Celebrate Saratoga's village charm with a handcrafted ADU that honors the community's artistic heritage and natural beauty.
          </p>
          <Button onClick={openCalendlyModal} className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-4 px-8 rounded-xl shadow-lg transition-colors text-lg">
            Book Free Consultation
          </Button>
        </section>

        {/* Internal Links */}
        <section className="py-16">
          <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
            <h3 className="text-2xl font-light text-white mb-6">Explore More ADU Options in Saratoga:</h3>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <Link to="/saratoga-adu" className="text-[#FF7A00] hover:underline">
                General ADU Services in Saratoga
              </Link>
              <Link to="/adu-cost-saratoga" className="text-[#FF7A00] hover:underline">
                ADU Cost in Saratoga
              </Link>
              <Link to="/adu-regulations-saratoga" className="text-[#FF7A00] hover:underline">
                ADU Regulations in Saratoga
              </Link>
            </div>
            
            <h3 className="text-2xl font-light text-white mb-6">Other Craftsman ADU Locations:</h3>
            <div className="grid md:grid-cols-4 gap-4">
              <Link to="/craftsman-adu-design-los-altos" className="text-[#FF7A00] hover:underline">
                Craftsman ADU Los Altos
              </Link>
              <Link to="/craftsman-adu-design-los-altos-hills" className="text-[#FF7A00] hover:underline">
                Craftsman ADU Los Altos Hills
              </Link>
              <Link to="/craftsman-adu-design-palo-alto" className="text-[#FF7A00] hover:underline">
                Craftsman ADU Palo Alto
              </Link>
              <Link to="/craftsman-adu-design-mountain-view" className="text-[#FF7A00] hover:underline">
                Craftsman ADU Mountain View
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingADUWizard />
      <FloatingReserveButton />
    </div>
  );
};

export default CraftsmanADUDesignSaratoga;